import { ArrowUpRight } from "lucide-react";
import { ventures } from "../../data/ventures";
import { MaskLines, FadeUp } from "../Reveal";

const Row = ({ v, index }) => (
  <FadeUp>
    <a
      href={v.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`venture-row-${v.slug}`}
      style={{ "--accent": v.accent }}
      className="group relative grid grid-cols-12 items-center gap-4 border-t border-bone/10 py-7 md:py-10"
    >
      <span className="col-span-2 font-mono text-xs text-bone/55 md:col-span-1">0{index + 1}</span>
      <span className="col-span-10 md:col-span-6">
        <span className="block font-display text-3xl font-extrabold uppercase leading-none tracking-tight transition-[transform,color] duration-500 group-hover:translate-x-3 group-hover:text-[var(--accent)] sm:text-5xl lg:text-6xl">
          {v.name}
        </span>
      </span>
      <img src={v.image} alt="" className="col-span-12 aspect-[16/9] w-full rounded-2xl object-cover md:hidden" />
      <span className="col-span-8 font-serif text-lg italic text-bone/75 md:col-span-3 md:text-xl">{v.sector}</span>
      <span className="col-span-4 flex items-center justify-end gap-5 md:col-span-2">
        <img
          src={v.logo}
          alt={`${v.name} logo`}
          data-testid={`venture-logo-${v.slug}`}
          className="hidden h-[104px] w-[104px] translate-x-3 scale-90 rounded-2xl bg-white object-cover opacity-0 shadow-2xl ring-1 ring-bone/20 transition-[opacity,transform] duration-500 group-hover:translate-x-0 group-hover:scale-100 group-hover:opacity-100 xl:block"
        />
        <span className="flex h-12 w-12 items-center justify-center rounded-full border border-bone/20 transition-[background-color,border-color,transform] duration-500 group-hover:rotate-45 group-hover:border-transparent group-hover:bg-[var(--accent)] group-hover:text-ink">
          <ArrowUpRight size={18} />
        </span>
      </span>
    </a>
  </FadeUp>
);

const Group = ({ title, items, offset }) => (
  <div className="mt-16">
    <div className="mb-2 flex items-baseline justify-between">
      <h3 className="eyebrow text-ember">{title}</h3>
      <span className="font-mono text-xs text-bone/55">{String(items.length).padStart(2, "0")}</span>
    </div>
    {items.map((v, i) => (
      <Row key={v.slug} v={v} index={offset + i} />
    ))}
  </div>
);

export const VenturesIndex = () => {
  const businesses = ventures.filter((v) => v.kind === "Business");
  const charities = ventures.filter((v) => v.kind === "Charity");

  return (
    <section
      id="ventures"
      data-testid="ventures-section"
      className="relative bg-ink px-5 py-24 text-bone md:px-10 md:py-36"
    >
      <div className="mx-auto max-w-[1500px]">
        <div className="grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <p className="eyebrow text-ember">The Ventures</p>
            <h2 className="mt-6 font-display text-5xl font-extrabold uppercase leading-[0.9] tracking-tight sm:text-6xl lg:text-[7rem]">
              <MaskLines lines={["Built to", <span key="l" className="font-serif font-normal normal-case italic text-ember">last.</span>]} />
            </h2>
          </div>
          <FadeUp className="self-end text-base leading-relaxed text-bone/75 lg:col-span-4">
            Three businesses and two charities. Tap any name to visit its website.
          </FadeUp>
        </div>
        <Group title="Businesses" items={businesses} offset={0} />
        <Group title="Charities" items={charities} offset={businesses.length} />
      </div>
    </section>
  );
};
